import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import * as _ from "lodash";
import { of } from "rxjs";
import { catchError, map, switchMap, tap, withLatestFrom } from "rxjs/operators";

import { VendorService } from "../../../services/api/vendor.service";
import { OpenActions } from "../../actions/open.actions";
import { OrderProposalActions } from "../../actions/order-proposal.actions";
import { FailState } from "../../models/fail-state";
import { VendorReviewState } from "../../models/open/vendor-review-state";
import { AppState } from "../../reducers";
import { OpenSelectors } from "../../selectors/open.selectors";

@Injectable()
export class VendorReviewsGetEffects {
	get = createEffect(() =>
		this.actions.pipe(
			ofType(OpenActions.vendorReviews.get),
			withLatestFrom(this.store.select(OpenSelectors.vendorReviews)),
			switchMap(([action, state]) => {
				if (this.canUseStoreValue(action, state)) {
					const vendorId = action.vendorId;
					const reviews = _.get(state.reviews, action.vendorId);
					return of(
						OpenActions.vendorReviews.getSuccess({ vendorId, reviews, loading: false })
					);
				} else {
					return of(OpenActions.vendorReviews.getApi({ ..._.omit(action, "type") }));
				}
			})
		)
	);

	getApi = createEffect(() =>
		this.actions.pipe(
			ofType(
				OpenActions.vendorReviews.getApi,
				OrderProposalActions.vendorReview.createSuccess
			),
			switchMap((action) => {
				const beforeTime = _.get(action, "beforeTime");
				const maxReviews = _.get(action, "maxReviews");
				return this.vendorService
					.vendorReviews(action.vendorId, beforeTime, maxReviews)
					.pipe(
						map((reviews) =>
							OpenActions.vendorReviews.getSuccess({
								vendorId: action.vendorId,
								reviews,
								loading: false
							})
						),
						catchError((error) =>
							of(OpenActions.vendorReviews.getFail(new FailState(error)))
						)
					);
			})
		)
	);

	getMany = createEffect(
		() =>
			this.actions.pipe(
				ofType(OpenActions.vendorReviews.getMany),
				withLatestFrom(this.store.select(OpenSelectors.vendorReviews)),
				tap(([action, state]) => {
					_.forEach(action.vendorIds, (vendorId) => {
						const existingReviews = _.get(state, ["reviews", vendorId]);
						if (existingReviews) {
							this.store.dispatch(
								OpenActions.vendorReviews.getSuccess({
									vendorId,
									reviews: existingReviews,
									loading: false
								})
							);
						} else {
							this.vendorService.vendorReviews(vendorId).subscribe((reviews) => {
								this.store.dispatch(
									OpenActions.vendorReviews.getSuccess({
										vendorId,
										reviews,
										loading: false
									})
								);
							});
						}
					});
				})
			),
		{ dispatch: false }
	);

	constructor(
		private actions: Actions,
		private store: Store<AppState>,
		private vendorService: VendorService
	) {}

	private canUseStoreValue(action, state: VendorReviewState): boolean {
		return (
			!_.has(state, "error") && !state.loading && _.has(state, ["reviews", action.vendorId])
		);
	}
}
